/* New file: Reusable PDFViewer component */
import React, { useState, useRef, useEffect, useContext } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Import worker directly to ensure it's included in the bundle
import 'pdfjs-dist/build/pdf.worker.entry';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { HttpRequestContext } from '../Blocks/HttpRequestProvider';

import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import HeightIcon from '@mui/icons-material/Height';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({ pdfUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [zoomMode, setZoomMode] = useState('horizontal'); // 'horizontal' or 'vertical'
  const pdfContainerRef = useRef(null);
  const { sendRequest } = useContext(HttpRequestContext);

  // Fetch PDF data when pdfUrl changes
  useEffect(() => {
    if (!pdfUrl) return;
    
    const fetchPdf = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await sendRequest({
          apiPath: pdfUrl,
          type: 'GET',
          responseType: 'blob',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        
        if (response instanceof Blob) {
          setPdfData(response);
        } else {
          setError('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching PDF:', err);
        setError('Error loading PDF: ' + (err.message || 'Unknown error'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPdf();
  }, [pdfUrl, sendRequest]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPageInputValue('1');
  };

  const handleDocumentLoadError = (error) => {
    console.error('Document load error:', error);
    setError('Error while loading document: ' + error.message);
  };

  const changePage = (offset) => {
    const newPageNumber = pageNumber + offset;
    if (newPageNumber >= 1 && newPageNumber <= numPages) {
      setPageNumber(newPageNumber);
      setPageInputValue(newPageNumber.toString());
    }
  };

  const jumpToPage = (event) => {
    if (event.key === 'Enter') {
      const pageNum = parseInt(pageInputValue, 10);
      if (pageNum >= 1 && pageNum <= numPages) {
        setPageNumber(pageNum);
      } else {
        setPageInputValue(pageNumber.toString());
      }
    }
  };

  const handlePageInputChange = (event) => {
    setPageInputValue(event.target.value);
  };

  const firstPage = () => {
    setPageNumber(1);
    setPageInputValue('1');
  };

  const lastPage = () => {
    setPageNumber(numPages);
    setPageInputValue(numPages.toString());
  };

  const rotateLeft = () => {
    setRotation((prev) => (prev - 90) % 360);
  };

  const rotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const toggleZoomMode = () => {
    setZoomMode(prevMode => prevMode === 'horizontal' ? 'vertical' : 'horizontal');
  };

  const downloadPDF = async () => {
    if (!pdfUrl) return;
    
    try {
      const response = await sendRequest({
        apiPath: pdfUrl,
        type: 'GET',
        responseType: 'blob'
      });
      
      if (response instanceof Blob) {
        // Create a temporary URL for download
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'document.pdf');
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const printPDF = async () => {
    if (!pdfUrl) return;
    
    try {
      const response = await sendRequest({
        apiPath: pdfUrl,
        type: 'GET',
        responseType: 'blob'
      });
      
      if (response instanceof Blob) {
        // Create a temporary URL for printing
        const url = window.URL.createObjectURL(response);
        const printWindow = window.open(url, '_blank');
        
        printWindow.onload = function() {
          printWindow.print();
          // Clean up URL after printing
          window.URL.revokeObjectURL(url);
        };
      }
    } catch (error) {
      console.error('Error printing PDF:', error);
    }
  };

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (pdfContainerRef.current.requestFullscreen) {
        pdfContainerRef.current.requestFullscreen();
      } else if (pdfContainerRef.current.mozRequestFullScreen) {
        pdfContainerRef.current.mozRequestFullScreen();
      } else if (pdfContainerRef.current.webkitRequestFullscreen) {
        pdfContainerRef.current.webkitRequestFullscreen();
      } else if (pdfContainerRef.current.msRequestFullscreen) {
        pdfContainerRef.current.msRequestFullscreen();
      }
      setIsFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullScreen(false);
    }
  };

  if (!pdfUrl) {
    return <Typography variant="body1">Aucun aperçu disponible. Commencez la conversation pour générer un aperçu.</Typography>;
  }

  return (
    <Box 
      ref={pdfContainerRef} 
      sx={{ 
        width: '100%', 
        height: isFullScreen ? '100vh' : 'auto',
        overflow: 'auto', // Ensure scrolling is enabled in all modes
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Paper sx={{ 
        padding: isFullScreen ? '5px' : '10px', 
        marginBottom: '10px', 
        display: 'flex', 
        flexWrap: 'wrap', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: '8px',
        flexShrink: 0 // Prevent toolbar from shrinking
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title="Première page">
            <IconButton onClick={firstPage} disabled={pageNumber <= 1} size="small">
              <FirstPageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Page précédente">
            <IconButton onClick={() => changePage(-1)} disabled={pageNumber <= 1} size="small">
              <NavigateBeforeIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
            <TextField
              size="small"
              value={pageInputValue}
              onChange={handlePageInputChange}
              onKeyDown={jumpToPage}
              inputProps={{ style: { textAlign: 'center', width: '40px' }, 'aria-label': 'page number' }}
              sx={{ width: '60px' }}
            />
            <Typography variant="body2" sx={{ mx: 1 }}>
              of {numPages || '?'}
            </Typography>
          </Box>
          <Tooltip title="Page suivante">
            <IconButton onClick={() => changePage(1)} disabled={pageNumber >= numPages} size="small">
              <NavigateNextIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dernière page">
            <IconButton onClick={lastPage} disabled={pageNumber >= numPages} size="small">
              <LastPageIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ height: 24, mx: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title="Rotate Left">
            <IconButton onClick={rotateLeft} size="small">
              <RotateLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rotate Right">
            <IconButton onClick={rotateRight} size="small">
              <RotateRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ height: 24, mx: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title="Download PDF">
            <IconButton onClick={downloadPDF} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print PDF">
            <IconButton onClick={printPDF} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ height: 24, mx: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={zoomMode === 'horizontal' ? "Ajuster à la hauteur" : "Ajuster à la largeur"}>
            <IconButton onClick={toggleZoomMode} size="small">
              {zoomMode === 'horizontal' ? <HeightIcon /> : <SwapHorizIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullScreen ? 'Quitter le plein écran' : 'Plein écran'}>
            <IconButton onClick={toggleFullScreen} size="small">
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ 
          overflow: 'auto', 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'flex-start',
          padding: '10px',
          backgroundColor: '#f5f5f5'
        }}>
          <Document
            key={`pdf-${Date.now()}`}
            file={pdfData}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={handleDocumentLoadError}
            loading={<CircularProgress />}
            options={{
              cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
              cMapPacked: true,
              standardFontDataUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/standard_fonts/`
            }}
          >
            <Page
              pageNumber={pageNumber}
              rotate={rotation}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={zoomMode === 'horizontal' ? (isFullScreen ? window.innerWidth - 50 : window.innerWidth - 80) : undefined}
              height={zoomMode === 'vertical' ? (isFullScreen ? window.innerHeight - 100 : window.innerHeight - 200) : undefined}
            />
          </Document>
        </Box>
      )}
    </Box>
  );
};

export default PDFViewer; 